import { selectors } from '@admin/app/store/selectors'
import { Component, ElementRef, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core'
import { IEmployerUser } from '@cnect/user-shared'
import { Permission } from '@employer/app/config/permission.config'
import { ROUTES } from '@employer/app/services/navigation.service'
import { AssistantGateService } from '@employer/app/modules/assistant/services/assistant-gate.service'
import { GuideService } from '@employer/app/modules/assistant/services/guide.service'
import { TourFocusService } from '@employer/app/modules/assistant/services/tour-focus.service'
import { AuthStore } from '@employer/app/store/auth.store'
import { environment } from '@employer/environments/environment'
import { PermissionStore } from '@engineering11/access-web'
import { CustomerPermissions, CustomerStore } from '@engineering11/customer-web'
import { E11ModalGlobalService } from '@engineering11/ui-lib/e11-modal'
import { Store } from '@ngrx/store'
import { Observable, Subscription, map } from 'rxjs'
import { userActions } from 'shared-lib'

@Component({
  selector: 'profile-dropdown',
  template: `
    <div
      class="e11-z-50 e11-flex e11-justify-end e11-items-center e11-gap-3 e11-relative"
      (closeOnClickOutside)="onClickOutside()"
      *ngrxLet="currentUser$ as currentUser"
    >
      <div
        (click)="dropDownOpen = !dropDownOpen"
        [ngClass]="{
          'e11-transition e11-ease-out e11-duration-200 e11-transform e11-opacity-0 e11-scale-95 e11-invisible': !dropDownOpen,
          'e11-transition e11-ease-in duration-75 e11-e11-transform e11-opacity-100 e11-scale-100': dropDownOpen
        }"
        class="e11-origin-top-right e11-absolute e11-right-0 e11-top-10 e11-mt-2 e11-w-48 e11-rounded-md e11-shadow-lg e11-py-1 e11-bg-white e11-border e11-border-skin-app-borders focus:e11-outline-none focus:e11-ring-opacity-100"
        role="menu"
        aria-orientation="vertical"
        aria-labelledby="user-menu-button"
        tabindex="-1"
      >
        <a
          [routerLink]="[routes.ACCOUNT]"
          data-tour-id="account-my-account"
          class="e11-block e11-px-4 e11-py-2 e11-text-sm e11-text-gray-700 hover:e11-bg-gray-100 e11-border-b e11-border-gray-200"
          role="menuitem"
          tabindex="-1"
          id="user-menu-item-0"
        >
          {{ 'My Account' | translate }}
        </a>
        <a
          [routerLink]="[routes.REPORTS]"
          class="e11-block e11-px-4 e11-py-2 e11-text-sm e11-text-gray-700 hover:e11-bg-gray-100 e11-border-b e11-border-gray-200"
          role="menuitem"
          tabindex="-1"
          id="user-menu-item-0"
        >
          {{ 'Reports' | translate }}
        </a>

        <!-- CNCT-6779: the tour's entry point on mobile, where the header's compass is hidden. Shown
             at every width so there is one place it always lives; the header keeps its compass on
             desktop. Same entitlement gate as that button — a free customer would get a half-working
             tour, so neither surface offers it. -->
        <button
          *ngIf="tourEntitled$ | async"
          type="button"
          data-tour-id="account-take-a-tour"
          class="e11-block e11-w-full e11-text-left e11-px-4 e11-py-2 e11-text-sm e11-text-gray-700 hover:e11-bg-gray-100 e11-border-b e11-border-gray-200 e11-bg-transparent e11-border-0 e11-cursor-pointer"
          role="menuitem"
          tabindex="-1"
          id="user-menu-item-tour"
          (click)="startTour()"
        >
          {{ 'Take a tour' | translate }}
        </button>

        <ng-container *ngrxLet="userPermissions$ as permissions">
          <!-- CNCT-6779: the All Company Settings entry was removed from this menu. The left-nav
               Company row is now a direct link to the same hub, so a second entry here was a
               duplicate route into it. -->

          <!-- Company manage permissions -->
          <ng-container *ngIf="permissions?.has(CustomerPermissions.ViewClient)">
            <a
              [routerLink]="[routes.CLIENT_MANAGEMENT]"
              class="e11-block e11-px-4 e11-py-2 e11-text-sm e11-text-gray-700 hover:e11-bg-gray-100 e11-border-b e11-border-gray-200"
              role="menuitem"
              tabindex="-1"
              id="user-menu-item-1"
            >
              {{ 'Client Management' | translate }}
            </a>
          </ng-container>
        </ng-container>

        <a
          href="#"
          class="e11-block e11-px-4 e11-py-2 e11-text-sm e11-text-gray-700 hover:e11-bg-gray-100 e11-border-b e11-border-gray-200"
          role="menuitem"
          tabindex="-1"
          id="user-menu-item-2"
          (click)="openQRCodeModal()"
        >
          {{ 'Sign in on mobile' | translate }}
        </a>

        <a
          href="#"
          class="e11-block e11-px-4 e11-py-2 e11-text-sm e11-text-gray-700 hover:e11-bg-gray-100"
          role="menuitem"
          tabindex="-1"
          id="user-menu-item-3"
          (click)="signOut()"
        >
          {{ 'Sign out' | translate }}
        </a>
      </div>

      <span
        (click)="dropDownOpen = !dropDownOpen"
        *ngrxLet="customerName$ as customerName"
        class="e11-flex e11-items-center sm:e11-visible e11-py-2 e11-rounded-md e11-text-sm e11-text-skin-primary-accent hover:e11-text-skin-primary e11-cursor-pointer"
      >
        <div class="e11-flex-col e11-min-w-[100px] e11-text-right">
          <p [ngClass]="!currentUser ? 'e11-mb-1' : 'e11-mb-0'" class="empty:e11-skeleton empty:e11-w-full">
            {{ currentUser?.firstName?.concat(' ') }}{{ currentUser?.lastName }}
          </p>
          <p class="e11-text-xs e11-text-skin-light e11-mb-0 empty:e11-skeleton empty:e11-w-full">{{ customerName }}</p>
        </div>
      </span>

      <!-- The avatar is the menu's affordance now, in the far-right slot the expand_more
           chevron used to hold; the name/company column right-aligns against it. -->
      <e11-avatar
        data-tour-id="header-account-menu"
        [imageUrl]="currentUser?.photoURL"
        [size]="'md'"
        [initials]="currentUser?.firstName!.charAt(0) + currentUser?.lastName!.charAt(0)"
        (click)="dropDownOpen = !dropDownOpen"
      ></e11-avatar>

      <ng-template #qrCodeModalContent>
        <ng-container *ngrxLet="customTokenUrl$ as customTokenUrl">
          <div class="e11-w-full e11-px-4">
            <div class="e11-flex e11-items-center e11-justify-center e11-mb-8 e11-flex-col e11-gap-2">
              <div class="e11-w-full e11-min-h-[200px] e11-flex e11-items-center e11-justify-center">
                <div *ngIf="!customTokenUrl" class="!e11-w-[200px] e11-bg-gray-400/40 !e11-h-[200px] e11-skeleton-empty"></div>
                <e11-qrcode
                  *ngIf="customTokenUrl"
                  [value]="customTokenUrl"
                  [width]="200"
                  [mobileWidth]="180"
                  [margin]="4"
                  [label]="'Login on mobile' | translate"
                ></e11-qrcode>
              </div>
              <div
                *ngIf="customTokenUrl"
                (click)="refreshCustomToken()"
                class="e11-flex e11-items-center e11-justify-center e11-cursor-pointer e11-text-xs e11-text-skin-primary-accent e11-w-full"
              >
                <span class="material-icons-outlined md-18">refresh</span> {{ 'Generate New QR Code' | translate }}
              </div>
              <p class="e11-mb-0 e11-text-sm">
                {{ "You're signing in as" | translate }} <strong>{{ currentUser?.email }}</strong>
              </p>
            </div>

            <div>
              <div class="e11-mb-8">
                <p class="e11-font-primary-demibold e11-mb-1">
                  {{
                    'Cnect for employers is available in the palm of your
                hand!' | translate
                  }}
                </p>
                <p class="e11-mb-2 e11-text-sm">{{ 'To access this workspace on your phone:' | translate }}</p>
                <ul class="e11-list-decimal e11-pl-8">
                  <li class="e11-mb-2">
                    <div class="lg:e11-mb-2">
                      {{ 'Download and open the' | translate }} <span class="e11-font-primary-medium">Cnected Employer</span>
                      {{ 'app on your mobile device.' | translate }}
                      <span class="e11-inline lg:e11-hidden"
                        >(<a
                          *ngIf="customTokenUrl"
                          class="e11-text-skin-primary-accent"
                          [href]="customTokenUrl + '&redirectToStore=true'"
                          target="_blank"
                        >
                          <span class="material-icons material-icons-outlined md-24">download</span> </a
                        >)</span
                      >
                    </div>

                    <div class="e11-gap-4 e11-hidden lg:e11-flex md:e11-w-2/3 2xl:e11-w-1/3 e11-mr-4 sm:e11-mr-0">
                      <a href="https://apps.apple.com/us/app/cnected-employer/id6502960629" target="_blank">
                        <img src="assets/images/ios_app_store_badge.svg" class="e11-mb-2 e11-mx-auto" />
                      </a>
                      <a href="https://play.google.com/store/apps/details?id=com.e11.prod.cnect_employer" target="_blank">
                        <img src="assets/images/google_play_badge.svg" class="e11-mb-2 e11-mx-auto" />
                      </a>
                    </div>
                  </li>
                  <li class="e11-mb-2">
                    {{ 'Tap' | translate }} <span class="e11-font-primary-medium">{{ 'Add a workspace' | translate }}</span>
                    {{ 'when prompted.' | translate }}
                  </li>
                  <li class="e11-mb-2">
                    {{ 'Tap' | translate }} <span class="e11-font-primary-medium">{{ 'Scan QR code' | translate }}</span
                    >{{ ' at the bottom of the list.' | translate }}
                  </li>
                  <li class="e11-mb-2">{{ 'Scan this QR code and complete sign in.' | translate }}</li>
                </ul>
              </div>

              <div class="e11-block lg:e11-hidden">
                <p class="e11-mb-2">{{ 'If you are already on your mobile device, you can click the link below instead:' | translate }}</p>
                <ul class="e11-list-disc e11-pl-8">
                  <li class="e11-mb-2">
                    <a [href]="customTokenUrl" target="_blank">{{ 'Login On App' | translate }}</a>
                  </li>
                </ul>
                <p>{{ 'Do not share this code, it is unique to you and your account login.' | translate }}</p>
              </div>
            </div>
          </div>
        </ng-container>
      </ng-template>
    </div>
  `,
})
export class ProfileDropdownComponent implements OnInit, OnDestroy {
  @ViewChild('qrCodeModalContent') qrCodeModalContent!: TemplateRef<any>
  routes = ROUTES
  dropDownOpen = false
  Permission = Permission
  CustomerPermissions = CustomerPermissions
  userPermissions$ = this.permissionStore.userPermissions$
  currentUser$: Observable<IEmployerUser | null> = this.store.select(selectors.getCurrentUser)

  customerName$ = this.customerStore.currentCustomer$.pipe(map(c => c?.name))
  customTokenUrl$ = this.authStore.customToken$.pipe(
    map(token => (token ? this.getDynamicLinkForToken(token.token, token.tenantId, token.tenantUrl) : null))
  )

  documentClickListener: any

  /**
   * The guided tour opens this menu for its account steps (CNCT-6771) and holds it open across them.
   * While it does, outside clicks must not close it: the tour's dim layer swallows the click, but the
   * event still reaches the capture-phase listener below with a target outside this component.
   */
  tourDrivenOpen = false
  private tourFocusSub?: Subscription

  constructor(
    private store: Store,
    private permissionStore: PermissionStore,
    private customerStore: CustomerStore,
    private modalService: E11ModalGlobalService,
    private authStore: AuthStore,
    private elementRef: ElementRef,
    private tourFocus: TourFocusService,
    private guide: GuideService,
    private assistantGate: AssistantGateService
  ) {
    this.authStore.onGetCustomToken()
  }

  /** Same gate the header's launcher uses: the tour is a trial/paid feature, and a free customer
   *  would get a half-working walk through a nav they do not have. */
  tourEntitled$ = this.assistantGate.gate$.pipe(map(gate => gate === 'entitled'))

  /** Close the menu first — the walk highlights chrome, and an open dropdown sits over it. */
  startTour(): void {
    this.dropDownOpen = false
    this.guide.start()
  }

  ngOnInit() {
    this.setupDocumentClickListener()
    // Steps 8-9 open the menu and keep it open; every other step (and the end of the tour) closes it.
    this.tourFocusSub = this.tourFocus.accountMenuOpen$.subscribe(open => {
      this.tourDrivenOpen = open
      this.dropDownOpen = open
    })
  }

  ngOnDestroy() {
    this.removeDocumentClickListener()
    this.tourFocusSub?.unsubscribe()
  }

  /** Dismiss on an outside click — unless the tour is currently holding the menu open. */
  onClickOutside() {
    if (!this.tourDrivenOpen) this.dropDownOpen = false
  }

  setupDocumentClickListener(): void {
    this.documentClickListener = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (this.dropDownOpen && !this.tourDrivenOpen && !this.elementRef.nativeElement.contains(target)) {
        this.dropDownOpen = false
      }
    }

    document.addEventListener('click', this.documentClickListener, true)
  }

  removeDocumentClickListener(): void {
    if (this.documentClickListener) {
      document.removeEventListener('click', this.documentClickListener, true)
    }
  }

  signOut() {
    this.store.dispatch(userActions.OnLogOut())
  }

  getDynamicLinkForToken(token: string, tenant: string, tenantUrl: string | undefined) {
    let appUrl = tenantUrl ?? environment.employerBaseUrl
    appUrl = appUrl.endsWith('/') ? appUrl : appUrl + '/'
    return `${environment.services.cloudFunctions}/dynamicLink/li/link?href=${appUrl}login-with-token/${token}&tenant=${tenant}&appKey=employer`
  }

  refreshCustomToken() {
    this.authStore.onGetCustomToken()
  }

  openQRCodeModal() {
    this.modalService.openModal({
      body: this.qrCodeModalContent,
      width: 'sm',
      title: 'Sign in on mobile',
    })
  }
}
